import axios from "axios";

// Fail loud if the backend URL is missing rather than silently hitting
// `/api/...` (relative) which would only work on the same host in dev.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  console.error(
    "[Ashlan] REACT_APP_BACKEND_URL is not set. Copy frontend/.env.example → .env and set your backend URL."
  );
  throw new Error(
    "REACT_APP_BACKEND_URL is required. See frontend/.env.example."
  );
}
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  // Send the HttpOnly admin session cookie on admin requests. Cookie is the
  // preferred auth path; the Bearer header remains during the transition
  // window and will be removed in a follow-up release.
  withCredentials: true,
});

// Public form submissions
export const submitContact = (data) => api.post("/contact", data).then((r) => r.data);
export const submitTestimonial = (data) =>
  api.post("/testimonials", data).then((r) => r.data);
export const submitProductRequest = (data) =>
  api.post("/product-requests", data).then((r) => r.data);
export const fetchTestimonials = () =>
  api.get("/testimonials").then((r) => r.data);

// A5 — Google reviews (cached, populated by background poller)
export const fetchGoogleReviews = () =>
  api.get("/reviews").then((r) => r.data);
export const adminRefreshReviews = () =>
  api.post("/reviews/admin/refresh", {}, authHeaders()).then((r) => r.data);
export const adminReviewsStatus = () =>
  api.get("/reviews/admin/status", authHeaders()).then((r) => r.data);

// Public blog
export const fetchPublicPosts = () =>
  api.get("/blog/posts").then((r) => r.data);
export const fetchPublicPost = (slug) =>
  api.get(`/blog/posts/${slug}`).then((r) => r.data);
export const searchPublicPosts = (q, limit = 20) =>
  api.get("/blog/search", { params: { q, limit } }).then((r) => r.data);

// A7 topics
export const fetchTopics = () =>
  api.get("/blog/topics").then((r) => r.data);
export const fetchTopic = (slug) =>
  api.get(`/blog/topics/${slug}`).then((r) => r.data);

// A11 comments (public)
export const fetchPostComments = (slug) =>
  api.get(`/blog/posts/${slug}/comments`).then((r) => r.data);
export const submitComment = (slug, body) =>
  api.post(`/blog/posts/${slug}/comments`, body).then((r) => r.data);

// A11 comments (admin)
export const adminListComments = (status) =>
  api
    .get("/blog/admin/comments", { ...authHeaders(), params: status ? { status } : {} })
    .then((r) => r.data);
export const adminCommentAction = (id, action) =>
  api.post(`/blog/admin/comments/${id}/${action}`, {}, authHeaders()).then((r) => r.data);

// E2/E3/E4 — Admin submissions
export const adminSubmissionsCounts = () =>
  api.get("/admin/submissions/counts", authHeaders()).then((r) => r.data);
export const adminListContacts = (status) =>
  api
    .get("/admin/contact", { ...authHeaders(), params: status ? { status } : {} })
    .then((r) => r.data);
export const adminUpdateContact = (id, body) =>
  api.patch(`/admin/contact/${id}`, body, authHeaders()).then((r) => r.data);
export const adminDeleteContact = (id) =>
  api.delete(`/admin/contact/${id}`, authHeaders()).then((r) => r.data);
export const adminReclassifyContact = (id) =>
  api.post(`/admin/contact/${id}/reclassify`, {}, authHeaders()).then((r) => r.data);
export const adminListProductRequests = (status) =>
  api
    .get("/admin/product-requests", { ...authHeaders(), params: status ? { status } : {} })
    .then((r) => r.data);
export const adminUpdateProductRequest = (id, body) =>
  api.patch(`/admin/product-requests/${id}`, body, authHeaders()).then((r) => r.data);
export const adminDeleteProductRequest = (id) =>
  api.delete(`/admin/product-requests/${id}`, authHeaders()).then((r) => r.data);
export const adminListTestimonials = (status) =>
  api
    .get("/admin/testimonials", { ...authHeaders(), params: status ? { status } : {} })
    .then((r) => r.data);
export const adminUpdateTestimonial = (id, body) =>
  api.patch(`/admin/testimonials/${id}`, body, authHeaders()).then((r) => r.data);
export const adminDeleteTestimonial = (id) =>
  api.delete(`/admin/testimonials/${id}`, authHeaders()).then((r) => r.data);

// A8 content refresh
export const adminRefreshCandidates = () =>
  api.get("/blog/admin/refresh-candidates", authHeaders()).then((r) => r.data);
export const adminSuggestRefresh = (postId) =>
  api
    .post(`/blog/admin/posts/${postId}/suggest-refresh`, {}, { ...authHeaders(), timeout: 180000 })
    .then((r) => r.data);
export const adminApplyRefresh = (postId) =>
  api.post(`/blog/admin/posts/${postId}/apply-refresh`, {}, authHeaders()).then((r) => r.data);

// Admin auth
const TOKEN_KEY = "ashlan_admin_token";
const EXPIRES_KEY = "ashlan_admin_expires";
export const setAdminToken = (t, expiresAt) => {
  if (t) {
    localStorage.setItem(TOKEN_KEY, t);
    if (expiresAt) localStorage.setItem(EXPIRES_KEY, expiresAt);
  } else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_KEY);
  }
};
export const getAdminToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const getAdminExpiresAt = () => localStorage.getItem(EXPIRES_KEY) || "";

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${getAdminToken()}` },
});

export const adminLogin = (email, password) =>
  api.post("/blog/admin/login", { email, password }).then((r) => r.data);
export const adminLogout = () =>
  api.post("/blog/admin/logout", {}, authHeaders()).then((r) => r.data);
export const adminMe = () => api.get("/blog/admin/me", authHeaders()).then((r) => r.data);
export const adminRefresh = () =>
  api.post("/blog/admin/refresh", {}, authHeaders()).then((r) => r.data);

// ---------- Staff Portal auth (cookie-only) ----------
export const staffLogin = (password) =>
  api.post("/staff/login", { password }).then((r) => r.data);
export const staffLogout = () =>
  api.post("/staff/logout").then((r) => r.data);
export const staffMe = () => api.get("/staff/me").then((r) => r.data);

export const adminListPosts = (status) =>
  api
    .get("/blog/admin/posts", { ...authHeaders(), params: status ? { status } : {} })
    .then((r) => r.data);
export const adminGetPost = (id) =>
  api.get(`/blog/admin/posts/${id}`, authHeaders()).then((r) => r.data);
export const adminEditPost = (id, body) =>
  api.patch(`/blog/admin/posts/${id}`, body, authHeaders()).then((r) => r.data);
export const adminPostAction = (id, body) =>
  api.post(`/blog/admin/posts/${id}/action`, body, authHeaders()).then((r) => r.data);
export const adminGenerateBatch = () =>
  api.post("/blog/admin/generate-batch", {}, authHeaders()).then((r) => r.data);
export const adminSummary = () =>
  api.get("/blog/admin/summary", authHeaders()).then((r) => r.data);

// E7 — bulk actions
export const adminBulkAction = (ids, action, extra = {}) =>
  api
    .post("/blog/admin/posts/bulk-action", { ids, action, ...extra }, authHeaders())
    .then((r) => r.data);

// E8 — per-post preview tokens
export const adminCreatePreviewToken = (postId) =>
  api
    .post(`/blog/admin/posts/${postId}/preview-token`, {}, authHeaders())
    .then((r) => r.data);
export const adminRevokePreviewToken = (postId) =>
  api
    .delete(`/blog/admin/posts/${postId}/preview-token`, authHeaders())
    .then((r) => r.data);
export const fetchPreviewPost = (token) =>
  api.get(`/blog/preview/${token}`).then((r) => r.data);

// D3 / D4 — Admin analytics
export const adminAnalyticsDimensions = () =>
  api.get("/analytics/admin/dimensions-catalog", authHeaders()).then((r) => r.data);
export const adminAnalyticsFunnel = (days = 30) =>
  api
    .get("/analytics/admin/funnel", { ...authHeaders(), params: { days } })
    .then((r) => r.data);
export const adminAnalyticsEvents = (params = {}) =>
  api
    .get("/analytics/admin/events", { ...authHeaders(), params })
    .then((r) => r.data);

// K3 — Learning Hub
export const fetchLearningResources = (audience = "clients") =>
  api.get("/learn/resources", { params: { audience } }).then((r) => r.data);
export const trackLearningTab = (tab) =>
  api.post("/learn/track-tab", { tab }).then((r) => r.data).catch(() => null);
export const adminListResources = (audience) =>
  api
    .get("/learn/admin/resources", { ...authHeaders(), params: audience ? { audience } : {} })
    .then((r) => r.data);
export const adminCreateResource = (body) =>
  api.post("/learn/admin/resources", body, authHeaders()).then((r) => r.data);
export const adminUpdateResource = (id, body) =>
  api.patch(`/learn/admin/resources/${id}`, body, authHeaders()).then((r) => r.data);
export const adminDeleteResource = (id) =>
  api.delete(`/learn/admin/resources/${id}`, authHeaders()).then((r) => r.data);
export const adminLearningTabStats = () =>
  api.get("/learn/admin/tab-stats", authHeaders()).then((r) => r.data);

// C7 — Concierge chatbot
export const conciergeChat = (body) =>
  api.post("/concierge/chat", body).then((r) => r.data);
export const conciergeHandoff = (body) =>
  api.post("/concierge/handoff", body).then((r) => r.data);
export const conciergeEvent = (body) =>
  api.post("/concierge/event", body).then((r) => r.data).catch(() => null);
export const conciergeLead = (body) =>
  api.post("/concierge/lead", body).then((r) => r.data);
export const conciergeLeadsSummary = () =>
  api.get("/concierge/leads/summary").then((r) => r.data);
export const conciergeEngagementSummary = () =>
  api.get("/concierge/engagement/summary").then((r) => r.data);

// G3 + G6 — Therabody product overrides
export const fetchProductOverrides = () =>
  api.get("/therabody/overrides").then((r) => r.data);
export const adminListProductOverrides = () =>
  api.get("/therabody/admin/overrides", authHeaders()).then((r) => r.data);
export const adminUpsertProductOverride = (slug, body) =>
  api.patch(`/therabody/admin/overrides/${slug}`, body, authHeaders()).then((r) => r.data);
export const adminDeleteProductOverride = (slug) =>
  api.delete(`/therabody/admin/overrides/${slug}`, authHeaders()).then((r) => r.data);

// B6 + B7 — Corporate Wellness inquiries
export const fetchCorporateOptions = () =>
  api.get("/corporate/options").then((r) => r.data);
export const submitCorporateInquiry = (body) =>
  api.post("/corporate/inquiries", body).then((r) => r.data);
export const adminListCorporateInquiries = (status) =>
  api
    .get("/corporate/admin/inquiries", { ...authHeaders(), params: status ? { status } : {} })
    .then((r) => r.data);
export const adminCorporateCounts = () =>
  api.get("/corporate/admin/counts", authHeaders()).then((r) => r.data);
export const adminUpdateCorporateInquiry = (id, body) =>
  api.patch(`/corporate/admin/inquiries/${id}`, body, authHeaders()).then((r) => r.data);
export const adminDeleteCorporateInquiry = (id) =>
  api.delete(`/corporate/admin/inquiries/${id}`, authHeaders()).then((r) => r.data);

// Early Access Preview (beta program)
export const betaState = () => api.get("/beta/state").then((r) => r.data);
export const betaUnlock = (body) => api.post("/beta/unlock", body).then((r) => r.data);
export const betaMe = () => api.get("/beta/me").then((r) => r.data);
export const betaFeedbackStart = (page_url) =>
  api.post("/beta/feedback/start", { page_url }).then((r) => r.data);
export const betaFeedbackMessage = (session_id, message) =>
  api.post("/beta/feedback/message", { session_id, message }).then((r) => r.data);
export const betaFeedbackFinish = (session_id, rating) =>
  api.post("/beta/feedback/finish", { session_id, rating }).then((r) => r.data);

// Beta admin
export const adminBetaStats = () =>
  api.get("/beta/admin/stats", authHeaders()).then((r) => r.data);
export const adminBetaTesters = () =>
  api.get("/beta/admin/testers", authHeaders()).then((r) => r.data);
export const adminBetaFeedback = (params = {}) =>
  api.get("/beta/admin/feedback", { ...authHeaders(), params }).then((r) => r.data);
export const adminBetaCreateInvites = (invites, send_emails = true) =>
  api
    .post("/beta/admin/invites", { invites, send_emails }, authHeaders())
    .then((r) => r.data);
export const adminBetaResyncMailchimp = () =>
  api.post("/beta/admin/resync-mailchimp", {}, authHeaders()).then((r) => r.data);
export const adminBetaRevoke = (testerId) =>
  api.delete(`/beta/admin/testers/${testerId}`, authHeaders()).then((r) => r.data);
export const betaApply = (body) =>
  api.post("/beta/applications", body).then((r) => r.data);
export const betaLaunchList = (body) =>
  api.post("/beta/launch-list", body).then((r) => r.data);
export const adminBetaApplications = (status) =>
  api
    .get("/beta/admin/applications", { ...authHeaders(), params: status ? { status } : {} })
    .then((r) => r.data);
export const adminBetaApproveApplication = (id) =>
  api.post(`/beta/admin/applications/${id}/approve`, {}, authHeaders()).then((r) => r.data);
export const adminBetaDeclineApplication = (id) =>
  api.post(`/beta/admin/applications/${id}/decline`, {}, authHeaders()).then((r) => r.data);

// Newsletter admin (MailChimp)
export const newsletterConfig = () =>
  api.get("/newsletter/admin/config", authHeaders()).then((r) => r.data);
export const newsletterAudiences = () =>
  api.get("/newsletter/admin/audiences", authHeaders()).then((r) => r.data);
export const newsletterList = () =>
  api.get("/newsletter/admin/list", authHeaders()).then((r) => r.data);
export const newsletterGet = (id) =>
  api.get(`/newsletter/admin/${id}`, authHeaders()).then((r) => r.data);
export const newsletterGenerate = (count = 3) =>
  api
    .post("/newsletter/admin/generate", { count }, { ...authHeaders(), timeout: 180000 })
    .then((r) => r.data);
export const newsletterUpdate = (id, body) =>
  api.patch(`/newsletter/admin/${id}`, body, authHeaders()).then((r) => r.data);
export const newsletterApprove = (id) =>
  api.post(`/newsletter/admin/${id}/approve`, {}, authHeaders()).then((r) => r.data);
export const newsletterReject = (id) =>
  api.post(`/newsletter/admin/${id}/reject`, {}, authHeaders()).then((r) => r.data);
export const newsletterPush = (id, body = {}) =>
  api
    .post(`/newsletter/admin/${id}/push-to-mailchimp`, body, authHeaders())
    .then((r) => r.data);
export const newsletterSchedule = (id, scheduleTime) =>
  api
    .post(`/newsletter/admin/${id}/schedule`, { schedule_time: scheduleTime }, authHeaders())
    .then((r) => r.data);
export const newsletterSendNow = (id) =>
  api.post(`/newsletter/admin/${id}/send-now`, {}, authHeaders()).then((r) => r.data);
export const newsletterDelete = (id) =>
  api.delete(`/newsletter/admin/${id}`, authHeaders()).then((r) => r.data);

// Voice (Vapi) — inbound call logs
export const voiceSummary = () =>
  api.get("/voice/admin/summary", authHeaders()).then((r) => r.data);
export const voiceCalls = (limit = 50) =>
  api.get("/voice/admin/calls", { ...authHeaders(), params: { limit } }).then((r) => r.data);
export const voiceCallDetail = (callId) =>
  api.get(`/voice/admin/calls/${callId}`, authHeaders()).then((r) => r.data);

// Invoice Generator (admin-only — Statement PDF -> Ashlan-formatted Invoice PDF)
export const invoiceGenUpload = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/admin/invoice-gen/upload", form, {
      ...authHeaders(),
      headers: { ...(authHeaders().headers || {}), "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};
export const invoiceGenGetSession = (sessionId) =>
  api.get(`/admin/invoice-gen/session/${sessionId}`, authHeaders()).then((r) => r.data);
export const invoiceGenSelectInvoice = (sessionId, invoiceNo) =>
  api
    .get(`/admin/invoice-gen/session/${sessionId}/invoice/${encodeURIComponent(invoiceNo)}`, authHeaders())
    .then((r) => r.data);
export const invoiceGenCheckDuplicate = (invoiceNo) =>
  api
    .get(`/admin/invoice-gen/records/check/${encodeURIComponent(invoiceNo)}`, authHeaders())
    .then((r) => r.data);
export const invoiceGenListRecords = () =>
  api.get("/admin/invoice-gen/records", authHeaders()).then((r) => r.data);
export const invoiceGenDownloadRecord = (recordId) =>
  api
    .get(`/admin/invoice-gen/records/${recordId}/download`, { ...authHeaders(), responseType: "blob" })
    .then((r) => r.data);
export const invoiceGenGenerate = (draft) =>
  api
    .post("/admin/invoice-gen/generate", draft, { ...authHeaders(), responseType: "blob" })
    .then((r) => r.data);
export const invoiceGenGetRecord = (id) =>
  api.get(`/admin/invoice-gen/records/${id}`, authHeaders()).then((r) => r.data);
export const invoiceGenUpdateRecord = (id, draft) =>
  api.patch(`/admin/invoice-gen/records/${id}`, draft, authHeaders()).then((r) => r.data);
export const invoiceGenSendEmail = (id, { recipientEmail, force } = {}) =>
  api
    .post(
      `/admin/invoice-gen/records/${id}/send-email`,
      { recipient_email: recipientEmail || null, force: !!force },
      authHeaders()
    )
    .then((r) => r.data);
export const invoiceGenGetSettings = () =>
  api.get("/admin/invoice-gen/settings", authHeaders()).then((r) => r.data);
export const invoiceGenUpdateSettings = (payment_instructions) =>
  api.put("/admin/invoice-gen/settings", { payment_instructions }, authHeaders()).then((r) => r.data);

// Invoice AI Assistant (admin-only — conversational orchestrator on top of
// the same deterministic renderer used by the manual flow above)
export const invoiceAiChat = (sessionId, message) =>
  api
    .post("/admin/invoice-ai/chat", { session_id: sessionId, message }, { ...authHeaders(), timeout: 90000 })
    .then((r) => r.data);
export const invoiceAiGetSession = (sessionId) =>
  api.get(`/admin/invoice-ai/sessions/${sessionId}`, authHeaders()).then((r) => r.data);
export const invoiceAiListSessions = () =>
  api.get("/admin/invoice-ai/sessions", authHeaders()).then((r) => r.data);
export const invoiceAiDeleteSession = (sessionId) =>
  api.delete(`/admin/invoice-ai/sessions/${sessionId}`, authHeaders()).then((r) => r.data);
export const invoiceAiResetDraft = (sessionId) =>
  api.post(`/admin/invoice-ai/sessions/${sessionId}/reset-draft`, {}, authHeaders()).then((r) => r.data);
export const invoiceAiUploadDocument = (sessionId, file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/admin/invoice-ai/sessions/${sessionId}/documents`, form, {
      ...authHeaders(),
      headers: { ...(authHeaders().headers || {}), "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};
export const invoiceAiSendEmail = (sessionId) =>
  api.post(`/admin/invoice-ai/sessions/${sessionId}/send-email`, {}, authHeaders()).then((r) => r.data);
export const invoiceAiListClients = () =>
  api.get("/admin/invoice-ai/clients", authHeaders()).then((r) => r.data);

